import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Material-UI Components
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Button,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Container,
    useTheme,
    useMediaQuery,
    alpha,
    Stack,
    Paper,
    Badge,
    Skeleton
} from '@mui/material';

// Material-UI Icons
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Security as SecurityIcon,
    Lock as LockIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Fingerprint as FingerprintIcon,
    Shield as ShieldIcon,
    AccountCircle as AccountCircleIcon,
    VpnKey as VpnKeyIcon,
    AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';

// Redux and utilities
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';
import { staticCountries } from '../utility/countries';
import { Eye, EyeOff, Copy, Wallet, Shield, TrendingDown, AlertCircle, CheckCircle, History, User, Mail, Phone, MapPin, Settings, Key, Smartphone, UserCheck } from 'lucide-react';

function PhoneComponent() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State management
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    
    // Form states
    const [phone, setPhone] = useState(userData?.phone ?? '');
    const [country, setCountry] = useState(userData?.country ?? '');
    const [phoneCode, setPhoneCode] = useState('...');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    
    // UI states
    const [showPin, setShowPin] = useState(false);
    
    // Custom theme colors for neon accents
    const neonColors = {
        primary: '#00D4FF',
        secondary: '#7C3AED',
        accent: '#F59E0B',
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        surface: '#1F2937',
        surfaceVariant: '#374151'
    };

    // Phone change handler
    const handlePhoneChange = async (e) => {
        e.preventDefault();
        
        if (!phone || !country) {
            notify('Please fill in all phone fields', 0);
            return;
        }
        
        if (!withdrawalPin) {
            notify('Please enter your PIN', 0);
            return;
        }

        setLoading(true);
        setLoadingText('Updating phone number...');
        
        dispatch(authActions.onLogin("account/update_phone", {country, phone, withdrawalPin}, response => {
            if (response.code === 1) {
                setWithdrawalPin('');
            }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    // Country change handler
    const handleCountryChange = (countryId) => {
        const selectedCountry = staticCountries.find(e => e.id == countryId);
        setCountry(countryId);
        setPhoneCode(selectedCountry?.phonecode || '...');
    };

    // Initialize component
    useEffect(() => {
        handleCountryChange(country);
    }, []);

    return (
        <SiteWide id="main" title="Update Phone Number">
            <Section loading={loading} overlayText={loadingText} title="Update Phone Number">
            <div className="min-h-screen p-4">
                <div className="max-w-4xl mx-auto pt-8">
                    {/* Modern Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                            Update Phone Number
                        </h1>
                        <p className="text-xl text-blue-200">
                            Modify your registered phone number for account security
                        </p>
                    </div>

                    <div className="flex flex-col gap-8">
                        {/* Enhanced Phone Update Card */}
                        <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                            <div className="relative overflow-hidden">
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
                                <div className="flex items-center mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                                        <Phone className="text-white" size={28} />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold text-white mb-2">Phone Verification</h2>
                                        <p className="text-gray-300">Keep your contact information up to date</p>
                                    </div>
                                </div>
                                
                                <form onSubmit={handlePhoneChange} className="space-y-6">
                                    <div>
                                        <label className="block text-white font-medium mb-3 text-lg">Country</label>
                                        <select
                                            value={country}
                                            onChange={(e) => handleCountryChange(e.target.value)}
                                            className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 text-white text-lg focus:outline-none focus:border-green-400 transition-all duration-300"
                                        >
                                            <option value="" className="bg-gray-800 text-white">Select Country</option>
                                            {staticCountries.map((country) => (
                                                <option key={country.id} value={country.id} className="bg-gray-800 text-white">
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white font-medium mb-3 text-lg">Phone Number</label>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300 text-lg font-medium">
                                                {phoneCode}
                                            </div>
                                            <input
                                                type="text"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="XXX XXX XXX"
                                                className="w-full bg-white/10 border-2 border-white/20 rounded-2xl pl-20 pr-6 py-4 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-green-400 transition-all duration-300"
                                            />
                                        </div>
                                        <p className="text-gray-400 text-sm mt-2">Enter your phone number without the country code</p>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-white font-medium mb-3 text-lg">PIN Verification</label>
                                        <div className="relative">
                                            <input
                                                type={showPin ? 'text' : 'password'}
                                                value={withdrawalPin}
                                                onChange={(e) => setWithdrawalPin(e.target.value)}
                                                maxLength={4}
                                                className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-yellow-400 transition-all duration-300"
                                                placeholder="Enter your 4-digit PIN"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPin(!showPin)}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                            >
                                                {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </button>
                                        </div>
                                        <p className="text-gray-400 text-sm mt-2">Enter your 4-digit PIN to confirm changes</p>
                                    </div>
                                    
                                    <div className="flex gap-4 pt-6">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`flex-1 py-4 px-6 rounded-2xl font-semibold text-white transition-all duration-300 text-lg ${
                                                loading 
                                                    ? 'bg-gray-500 cursor-not-allowed' 
                                                    : 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 hover:scale-105 shadow-lg hover:shadow-green-500/25'
                                            }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                    Updating...
                                                </div>
                                            ) : (
                                                'Update Phone'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            </Section>
        </SiteWide>
    );
}

export default PhoneComponent;